import { App } from '@capacitor/app'
import { platform } from '../device/platform'
import { splitRoute } from './splitRoute'
import { deepLinkStore } from './DeepLinkStore'
import { DEEPLINK_ROUTES } from './routes'

type NavigateFn = (path: string) => void

let _navigate: NavigateFn | null = null

/**
 * Procesa una URL de deeplink (desde appUrlOpen o OneSignal click).
 * Si hay navigateFn registrada, navega directamente.
 * Si no (usuario no logueado), guarda en DeepLinkStore para post-login.
 */
export function processDeepLinkUrl (url: string): void {
  const { route, hashParams } = splitRoute(url)

  // SSO login via deeplink (token en query params)
  // Se maneja por el flujo normal de auth, no acá
  if (url.includes('token=')) return

  const internalRoute = DEEPLINK_ROUTES[route]

  // Guardar params siempre (field, farm, post, install, etc.)
  if (Object.keys(hashParams).length > 0) {
    deepLinkStore.setParams(hashParams)
  }

  // Si la ruta es null (ej: 'image'), solo guardamos params
  if (internalRoute === null || internalRoute === undefined) {
    // Para rutas no mapeadas pero con ruta válida, intentar navegar directo
    if (internalRoute === undefined && route) {
      const path = `/${route}`
      if (_navigate != null) {
        _navigate(path)
      } else {
        deepLinkStore.setPendingRoute(path)
      }
    }
    return
  }

  if (_navigate != null) {
    _navigate(internalRoute)
  } else {
    deepLinkStore.setPendingRoute(internalRoute)
  }
}

/**
 * Inicializa el listener de deep links nativos (appUrlOpen).
 * Debe llamarse una sola vez al montar la app.
 */
export function initDeepLinks (): void {
  if (platform === 'web') return

  App.addListener('appUrlOpen', (event) => {
    if (event.url) {
      processDeepLinkUrl(event.url)
    }
  })
}

/**
 * Registra la función de navegación. Se llama cuando NavigationProvider está listo.
 * Además, consume cualquier ruta pendiente que haya llegado antes del login.
 */
export function setDeepLinkNavigator (navigateFn: NavigateFn): void {
  _navigate = navigateFn

  // Consumir ruta pendiente si existe (deeplink llegó antes de que la app estuviera lista)
  const pending = deepLinkStore.consumePendingRoute()
  if (pending != null) {
    setTimeout(() => { navigateFn(pending) }, 300)
  }
}

export { deepLinkStore } from './DeepLinkStore'
export { splitRoute } from './splitRoute'
